import { Drawer } from "app/components/ui/drawer/Drawer";
import { Fragment, memo, useCallback, useRef, useState } from "react";
import { useField } from "formik";
import classNames from "classnames";
import PropTypes from "prop-types";
import { messagePropType } from "app/utils/propTypes";
import AdvancedSelectFormik from "app/pages/.shared/form/AdvancedSelectFormik/AdvancedSelectFormik";
import "./SmartDPInputSidePanelContent.scss";
import isEmpty from "lodash/isEmpty";
import useHidePartnerCustomHeader from "app/utils/hooks/useHidePartnerCustomHeader";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import useBlockScroll from "app/utils/hooks/useBlockScroll";

const SmartDPInputSidePanelContent = props => {
	const [isOpen, setOpen] = useState(false);
	const {
		id,
		label,
		className,
		isRequired,
		drawerInputLabel,
		drawerTitleLabel,
		getOptionLabel = () => {},
		isSearchable = true,
	} = props;
	const [field, meta, helpers] = useField(props);
	const { setError } = helpers;
	const selectRef = useRef();
	useBlockScroll(isOpen);
	useHidePartnerCustomHeader({ hide: isOpen });

	const isTouched = field.value;

	const inputClassName = classNames("smartdp-input-search-select", className, {
		"smartdp-input-search-select--required": isRequired,
		"smartdp-input-search-select--touched": isTouched,
		"smartdp-input-search-select--error": meta.touched && meta.error,
	});

	const handleInputValueChange = useCallback(option => {
		helpers.setValue(option);
		helpers.setTouched(true);
		if (!isEmpty(option)) {
			handleOpenChange(false);
		}
	}, []);

	const handleOpenChange = useCallback(isOpen => {
		if (isOpen) {
			setOpen(true);
			setError();
		} else {
			setOpen(false);
		}
	}, []);

	return (
		<Fragment>
			<button
				type="button"
				className={inputClassName}
				onClick={() => handleOpenChange(true)}
				data-testId={props["data-testid"]}
			>
				<label htmlFor={id} className="smartdp-input-search-select__label">
					<Typography
						className="smartdp-input-search-select__label-text"
						variant={
							isTouched ? TYPOGRAPHY_VARIANTS.XSMALL : TYPOGRAPHY_VARIANTS.REGULAR
						}
					>
						{label}
					</Typography>
				</label>
				{field.value && (
					<div className="smartdp-input-search-select__input">
						{getOptionLabel(field.value)}
					</div>
				)}
			</button>

			<Drawer.Root
				open={isOpen}
				onOpenChange={handleOpenChange}
				className="smartDP-input-side-panel"
			>
				<Drawer.Content className="smartDP-input-side-panel__content">
					<Drawer.Header>
						<Typography variant={TYPOGRAPHY_VARIANTS.XL} isBold>
							{drawerTitleLabel?.length > 0 ? drawerTitleLabel : label}
						</Typography>
					</Drawer.Header>

					<Drawer.Body>
						<AdvancedSelectFormik
							// Use defaultMenuIsOpen instead of menuIsOpen to allow clear button to work on first tap
							defaultMenuIsOpen={true}
							menuIsOpen={true}
							{...props}
							label={drawerInputLabel ? drawerInputLabel : label}
							autoFocus={false}
							isClearable={isSearchable}
							selectRef={selectRef}
							classNamePrefix="advanced-select__select-sdp"
							onChange={handleInputValueChange}
							captureMenuScroll={false}
							menuPortalTarget={null} // mode portal par défaut, on force à null pour éviter pb affichage sur mobile quand le side panel s'ouvre
						/>
					</Drawer.Body>
				</Drawer.Content>
			</Drawer.Root>
		</Fragment>
	);
};

SmartDPInputSidePanelContent.propTypes = {
	id: PropTypes.string,
	label: messagePropType,
	value: PropTypes.any,
	"data-testid": PropTypes.string,
	isSearchable: PropTypes.bool,
	getOptionLabel: PropTypes.func,
	isRequired: PropTypes.bool,
	drawerInputLabel: messagePropType,
	drawerTitleLabel: messagePropType,
};

export default memo(SmartDPInputSidePanelContent);
